<template>
  <div class="stock-in-order-container">
    <!-- 头部：适配PDA握持姿势，返回键便于拇指操作 -->
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">入库订单</h1>
      <div class="header-right"></div>
    </header>
    
    <main class="content">
      <!-- 状态筛选：高频操作按钮放大，避免误触 -->
      <div class="status-filter">
        <button 
          :class="['filter-btn', { active: currentStatus === '0' }]"
          @click="switchStatus('0')"
        >
          待分配
        </button>
        <button 
          :class="['filter-btn', { active: currentStatus === '6' }]"
          @click="switchStatus('6')"
        >
          正在入库中
        </button>
        <button 
          :class="['filter-btn', { active: currentStatus === '3' }]"
          @click="switchStatus('3')"
        >
          已完成
        </button>
      </div>
      
      <!-- 订单列表：放大行高，优化滑动体验 -->
      <div class="stock-in-list">
        <div class="list-header">
          <span class="header-item">单号</span>
          <span class="header-item">创建时间</span>
          <span class="header-item" v-if="currentStatus === '0' || currentStatus === '6'">操作</span>
        </div>
        <div class="list-body">
          <!-- 加载状态：支持点击重试 -->
          <div v-if="loading === true" class="loading">加载中...</div>
          <div 
            v-else-if="loading === 'retry'" 
            class="loading retry"
            @click="loadStockInOrderList()"
          >
            网络异常，点击重试
          </div>
          <div v-else-if="stockInOrderList.length === 0" class="empty">暂无数据</div>
          <!-- 列表项：触控区域≥60px，增加点击反馈 -->
          <div 
            v-for="item in stockInOrderList" 
            :key="item.id"
            class="list-item"
            :class="{ 'item-active': activeItemId === item.id }"
          >
            <span class="list-item-text" @click="showStockInOrderDetail(item.id)">{{ item.batchNo }}</span>
            <span class="list-item-text" @click="showStockInOrderDetail(item.id)">{{ formatTime(item.createTime) }}</span>
            <button 
              v-if="currentStatus === '0' || currentStatus === '6'"
              class="collect-btn"
              @click.stop="handleConfirmCollect(item.id)"
              :disabled="confirmingIds.includes(item.id) || item.wareInStatus !== 0"
            >
              {{ confirmingIds.includes(item.id) ? '确认中...' : '确认收货' }}
            </button>
          </div>
        </div>
      </div>
      
      <!-- 分页：放大按钮，适配单手操作 -->
      <div class="pagination" v-if="!loading && stockInOrderList.length > 0">
        <button 
          class="page-btn"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        >
          上一页
        </button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <button 
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
        >
          下一页
        </button>
      </div>
    </main>
    
    <!-- 详情弹窗：全屏化设计，减少视觉层级 -->
    <div v-if="showDetailModal" class="detail-modal" @click="closeDetailModal">
      <div class="detail-modal-content" @click.stop>
        <div class="modal-header">
          <h3>入库详情</h3>
          <!-- 关闭按钮：放大至40x40px，便于拇指点击 -->
          <button class="close-btn" @click="closeDetailModal">×</button>
        </div>
        <div class="modal-body">
          <div v-if="detailLoading" class="loading">加载中...</div>
          <div v-else-if="stockInOrderDetail" class="detail-wrapper">
            <!-- 基础信息：网格布局，适配PDA横向紧凑屏幕 -->
            <div class="detail-info">
              <div class="info-row">
                <span class="info-label">批次号：</span>
                <span class="info-value">{{ stockInOrderDetail.batchNo }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">创建时间：</span>
                <span class="info-value">{{ formatTime(stockInOrderDetail.createTime) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">供应商：</span>
                <span class="info-value">{{ stockInOrderDetail.supplierName }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">仓库：</span>
                <span class="info-value">{{ stockInOrderDetail.storeName }}</span>
              </div>
            </div>
            
            <!-- 物料明细：放大单元格，便于查看 -->
            <h4 class="material-title">物料明细</h4>
            <div class="material-list">
              <div class="material-header">
                <span>物料编码</span>
                <span>批次</span>
                <span>数量</span>
              </div>
              <div 
                v-for="(material, index) in stockInOrderDetail.wareInDetailList" 
                :key="index"
                class="material-item"
              >
                <span class="material-text">{{ material.materialNo }}</span>
                <span class="material-text">{{ material.batchNo }}</span>
                <span class="material-text">{{ material.wareInNum }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getStockInOrderList, getStockInOrderDetail, confirmCollect } from '@/api/inOrder';

export default {
  name: 'StockInOrder',
  data() {
    return {
      currentStatus: '0', // 默认显示待分配状态
      currentPage: 1,
      pageSize: 10, // PDA屏幕小，减少单页条数
      totalPages: 1,
      stockInOrderList: [],
      loading: false,
      showDetailModal: false,
      stockInOrderDetail: null,
      detailLoading: false,
      activeItemId: null, // 用于触摸反馈
      touchTimer: null, // 触摸计时
      confirmingIds: [] // 正在确认收货的订单ID列表
    };
  },
  mounted() {
    this.loadStockInOrderList();
    // PDA适配：禁止页面缩放（防止误触手势）
    document.addEventListener('touchmove', (e) => {
      if (e.touches.length > 1) e.preventDefault();
    }, { passive: false });
  },
  methods: {
    // 返回按钮：增加PDA物理键兼容
    handleBack() {
      this.$router.back();
    },
    
    // 切换状态：PDA快速切换，增加状态反馈
    switchStatus(status) {
      if (this.currentStatus === status) return;
      this.currentStatus = status;
      this.currentPage = 1;
      this.loadStockInOrderList();
    },
    
    // 加载列表：优化PDA网络波动处理
    loadStockInOrderList() {
      this.loading = true;
      getStockInOrderList({
        wareInStatus: this.currentStatus,
        current: this.currentPage,
        size: this.pageSize
      })
        .then(res => {
          if (res.success && res.data) {
            this.stockInOrderList = res.data.records || [];
            this.totalPages = Math.ceil(res.data.total / this.pageSize) || 1;
          } else {
            this.$toast('数据加载失败，请重试');
          }
        })
        .catch(error => {
          console.error('获取入库订单列表失败:', error);
          this.loading = 'retry'; // 标记为可重试状态
        })
        .finally(() => {
          if (this.loading !== 'retry') this.loading = false;
        });
    },
    
    // 分页：PDA单手操作优化
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.loadStockInOrderList();
    },
    
    // 显示详情：PDA快速查看优化
    showStockInOrderDetail(id) {
      this.detailLoading = true;
      getStockInOrderDetail(id)
        .then(res => {
          if (res.success && res.data) {
            this.stockInOrderDetail = res.data;
            this.showDetailModal = true;
          } else {
            this.$toast('详情加载失败，请重试');
          }
        })
        .catch(error => {
          console.error('获取入库订单详情失败:', error);
          this.$toast('网络异常，无法加载详情');
        })
        .finally(() => {
          this.detailLoading = false;
        });
    },
    
    // 关闭弹窗：PDA点击空白关闭
    closeDetailModal() {
      this.showDetailModal = false;
      this.stockInOrderDetail = null;
    },
    
    // PDA时间格式化：简化显示（去掉秒）
    formatTime(time) {
      if (!time) return '-';
      return time.replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}):\d{2}/, '$1');
    },
    
    // 列表项触摸开始：增强PDA触摸反馈
    handleItemTouchStart(id) {
      this.activeItemId = id;
      // 防止快速点击触发多次
      this.touchTimer = setTimeout(() => {
        this.activeItemId = id;
      }, 50);
    },
    
    // 列表项触摸结束：清除反馈状态
    handleItemTouchEnd() {
      clearTimeout(this.touchTimer);
      setTimeout(() => {
        this.activeItemId = null;
      }, 100);
    },
    
    // 确认收货操作
    handleConfirmCollect(id) {
      // 添加到正在确认的列表中，防止重复点击
      this.confirmingIds.push(id);
      
      // 显示确认对话框
      if (window.confirm('确定要确认收货吗？')) {
        confirmCollect(id)
          .then(res => {
            if (res.success) {
              this.$toast('确认收货成功');
              // 刷新当前页面数据
              this.loadStockInOrderList();
            } else {
              this.$toast(res.msg || '确认收货失败');
            }
          })
          .catch(error => {
            console.error('确认收货失败:', error);
            this.$toast('网络异常，确认收货失败');
          })
          .finally(() => {
            // 从正在确认的列表中移除
            const index = this.confirmingIds.indexOf(id);
            if (index > -1) {
              this.confirmingIds.splice(index, 1);
            }
          });
      } else {
        // 从正在确认的列表中移除
        const index = this.confirmingIds.indexOf(id);
        if (index > -1) {
          this.confirmingIds.splice(index, 1);
        }
      }
    }
  },
  // 监听PDA物理按键
  created() {
    const that = this;
    document.addEventListener('keydown', function(e) {
      // 物理返回键（键码8）
      if (e.keyCode === 8) {
        e.preventDefault();
        if (that.showDetailModal) {
          that.closeDetailModal();
        } else {
          that.handleBack();
        }
      }
      // 物理扫描键（部分PDA有专用扫描键，键码可能为13）
      if (e.keyCode === 13 && that.showDetailModal) {
        e.preventDefault();
        that.closeDetailModal();
      }
    });
  },
  // 移除事件监听
  beforeUnmount() {
    document.removeEventListener('keydown', () => {});
    document.removeEventListener('touchmove', () => {});
  }
};
</script>

<style scoped>
/* 基础容器样式 */
.stock-in-order-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  background-color: #f5f7fa;
  color: #333;
  font-size: 14px;
}

/* 头部样式 - 与扫码入库页面保持一致 */
.header {
  display: flex;
  align-items: center;
  padding: 3px;
  background-color: #032566;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
}

.icon-back {
  width: 24px;
  height: 24px;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.back-btn:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

.title {
  flex: 1;
  font-size: 18px;
  text-align: center;
  margin: 0;
  font-weight: 600;
}

.header-right {
  width: 40px; /* 保持标题居中 */
}

/* 内容区样式 */
.content {
  flex: 1;
  padding: 10px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* 状态筛选样式 */
.status-filter {
  display: flex;
  gap: 8px;
  background-color: #fff;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.filter-btn {
  flex: 1;
  padding: 16px 10px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background-color: #f5f5f5;
  color: #333;
}

.filter-btn.active {
  background-color: #1890ff;
  color: #fff;
  box-shadow: 0 2px 8px rgba(24,144,255,0.3);
}

/* 订单列表样式 */
.stock-in-list {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.list-header {
  display: flex;
  padding: 14px 15px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-weight: 700;
  font-size: 16px;
  color: #333;
}

.header-item {
  flex: 1;
  text-align: left;
}

.list-body {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.list-item {
  display: flex;
  padding: 18px 15px;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s;
}

.list-item.item-active {
  background-color: #e6f7ff;
}

.list-item-text {
  flex: 1;
  font-size: 15px;
  color: #333;
  word-break: break-all;
}

/* 确认收货按钮样式 */
.collect-btn {
  min-width: 100px;
  padding: 8px 16px;
  margin-left: 10px;
  background-color: #42b983;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.collect-btn:hover:not(:disabled) {
  background-color: #359469;
}

.collect-btn:disabled {
  background-color: #a0d4ae;
  cursor: not-allowed;
}

/* 分页样式 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
  gap: 12px;
  padding: 8px 0;
}

.page-btn {
  width: 100px;
  padding: 12px 0;
  border: 1px solid #d9d9d9;
  background-color: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.page-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
  background-color: #f5f5f5;
  color: #999;
}

.page-info {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  padding: 0 8px;
}

/* 加载/空状态样式 */
.loading, .empty {
  padding: 60px 0;
  text-align: center;
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

.loading.retry {
  color: #1890ff;
  cursor: pointer;
}

/* 详情弹窗样式 */
.detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.detail-modal-content {
  background-color: #fff;
  border-radius: 8px;
  width: 96%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.close-btn {
  width: 40px;
  height: 40px;
  padding: 0;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.modal-body {
  padding: 15px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
}

.detail-wrapper {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.detail-info {
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 6px;
}

.info-row {
  display: flex;
  margin-bottom: 12px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  flex: 0 0 90px;
  font-weight: 600;
  color: #333;
  font-size: 15px;
}

.info-value {
  flex: 1;
  font-size: 15px;
  color: #333;
  word-break: break-all;
}

.material-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin: 0;
  padding: 5px 0;
}

.material-list {
  background-color: #fff;
  border-radius: 6px;
  overflow: hidden;
}

.material-header {
  display: flex;
  padding: 12px 15px;
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 15px;
}

.material-header span {
  flex: 1;
  text-align: left;
}

.material-item {
  display: flex;
  padding: 14px 15px;
  border-top: 1px solid #e0e0e0;
  font-size: 15px;
}

.material-text {
  flex: 1;
  color: #333;
  word-break: break-all;
}

/* PDA横屏适配 */
@media (orientation: landscape) {
  .list-body {
    max-height: calc(100vh - 180px);
  }
  
  .detail-modal-content {
    max-height: 85vh;
  }
}
</style>
